import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { UserRole } from 'types/userRoles';
import { Button, Chip } from 'common/components';
import { Table, TableColumn } from 'common/components/Table';
import { AddUserModal } from '../AddUserModal';
import { InviteUserModal } from '../InviteUserModal';
import { UsersSectionProps, MappedUser } from './UsersSection.types';
import styles from './UsersSection.module.scss';

const ROLE_LABEL_MAP: Record<string, string> = {
    [UserRole.SUPER_ADMIN]: 'Super Admin',
    [UserRole.ADMIN]: 'Admin',
    [UserRole.MANAGER]: 'Manager',
    [UserRole.AGENT]: 'Agent',
};

const ROLE_ORDER = [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.AGENT];

const getRoleLabels = (roles: string[]): string => {
    return ROLE_ORDER.filter((r) => roles.includes(r))
        .map((r) => ROLE_LABEL_MAP[r])
        .join(', ');
};

const UsersSection = ({
    users,
    isTenantLoading,
    onEditUser,
    onDeleteUser,
    isAddUserModalOpen,
    onOpenAddUserModal,
    onCloseAddUserModal,
    onAddUserSuccess,
    onAddUserError,
    isInviteUserModalOpen,
    onOpenInviteUserModal,
    onCloseInviteUserModal,
    onInviteUserSuccess,
    onInviteUserError,
    hasTenantAccess,
    tenants,
    selectedTenant,
    selectedUser,
    currentUserTenantId,
}: UsersSectionProps) => {
    const columns: TableColumn<MappedUser>[] = [
        {
            key: 'username',
            title: 'Username',
            render: (_, record) => `${record.firstName} ${record.lastName}`,
        },
        {
            key: 'email',
            title: 'Email',
            dataIndex: 'email',
        },
        {
            key: 'role',
            title: 'Role',
            render: (_, record) => getRoleLabels(record.roles),
        },
        {
            key: 'status',
            title: 'Status',
            render: (_, record) =>
                record.inviteStatus === 'pending' ? (
                    <Chip variant='filled' color='warning' size='sm'>
                        Pending
                    </Chip>
                ) : (
                    <Chip variant='filled' color='success' size='sm'>
                        Active
                    </Chip>
                ),
        },
        {
            key: 'actions',
            title: 'Actions',
            render: (_, record) => (
                <div className={styles.actionsCell}>
                    <Button
                        variant='outline-secondary'
                        size='sm'
                        icon={Pencil}
                        iconOnly
                        onClick={() => onEditUser(record)}
                        title='Edit user'
                    />
                    <Button
                        variant='outline-secondary'
                        size='sm'
                        icon={Trash2}
                        iconOnly
                        onClick={() => onDeleteUser(record)}
                        title='Delete user'
                    />
                </div>
            ),
        },
    ];

    return (
        <>
            <div className={styles.usersHeader}>
                <h2>Users {selectedTenant ? `for ${selectedTenant.name}` : ''}</h2>
                <div className={styles.headerActions}>
                    <Button variant='outline-secondary' size='sm' onClick={onOpenInviteUserModal}>
                        Invite User
                    </Button>
                    <Button variant='primary' size='sm' onClick={onOpenAddUserModal}>
                        Add User
                    </Button>
                </div>
            </div>
            <Table
                data={users}
                columns={columns}
                isLoading={isTenantLoading}
                emptyText='No users found'
                rowKey='id'
                hoverable
                variant='secondary'
                className={styles.usersTableWrapper}
            />

            {isAddUserModalOpen && (
                <AddUserModal
                    isOpen={isAddUserModalOpen}
                    onClose={onCloseAddUserModal}
                    onSuccess={onAddUserSuccess}
                    onError={onAddUserError}
                    hasTenantAccess={hasTenantAccess}
                    tenants={tenants}
                    selectedTenant={selectedTenant}
                    selectedUser={selectedUser}
                    currentUserTenantId={currentUserTenantId}
                />
            )}

            {isInviteUserModalOpen && (
                <InviteUserModal
                    isOpen={isInviteUserModalOpen}
                    onClose={onCloseInviteUserModal}
                    onSuccess={onInviteUserSuccess}
                    onError={onInviteUserError}
                    hasTenantAccess={hasTenantAccess}
                    tenants={tenants}
                    selectedTenant={selectedTenant}
                    selectedUser={selectedUser}
                    currentUserTenantId={currentUserTenantId}
                />
            )}
        </>
    );
};

export default UsersSection;
